import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { payoutService } from "../../services/payout.service";
import { format } from "date-fns";
import { toast } from "react-hot-toast";
import { DataTable, DataTablePagination } from "@/components/ui/data-table";
import { PayoutAmount } from "@/components/payouts/PayoutAmount";
import type { PayoutBreakdown } from "../../services/payout.service";

interface PayoutHistoryItem {
  id: string;
  affiliateId: string;
  name: string;
  username: string;
  avatar: string;
  email: string;
  payoutStatus: string;
  status: string;
  paymentMethod: string;
  paymentInformation: string;
  code: string;
  amount: number;
  breakdown?: PayoutBreakdown | null;
  payoutDate: string;
  paidAt?: string | null;
  referenceNumber: string | null;
  company: {
    companyName: string;
    brandProfileImage: string;
    description: string;
  };
  // Original data for detail view
  paymentDetails?: {
    fieldData: Record<string, string>;
    paymentMethodId: string;
    paymentMethodName: string;
  };
}

const columns: ColumnDef<PayoutHistoryItem>[] = [
  {
    id: "company",
    header: "Company",
    cell: ({ row }) => (
      <div className="flex items-center gap-3">
        <img
          src={row.original.company.brandProfileImage}
          alt={row.original.company.companyName}
          className="h-8 w-8 rounded-full"
        />
        <span className="truncate font-medium text-foreground">
          {row.original.company.companyName}
        </span>
      </div>
    ),
    meta: {
      width: "w-[45%]",
      skeleton: (
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 shrink-0 rounded-full bg-muted animate-pulse" />
          <div className="h-3.5 w-32 rounded bg-muted animate-pulse" />
        </div>
      ),
    },
  },
  {
    accessorKey: "amount",
    header: "Amount",
    cell: ({ row }) => (
      <PayoutAmount amount={row.original.amount} breakdown={row.original.breakdown} />
    ),
    meta: { width: "w-[27%]" },
  },
  {
    accessorKey: "payoutDate",
    header: "Payout date",
    cell: ({ row }) => format(new Date(row.original.payoutDate), "MM/dd/yy"),
    meta: { width: "w-[28%]" },
  },
];

export default function PayoutHistory() {
  const navigate = useNavigate();
  const [payoutHistory, setPayoutHistory] = useState<PayoutHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Track if we're currently fetching to prevent duplicate calls
  const isFetchingRef = useRef(false);

  useEffect(() => {
    fetchPayoutHistory(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const fetchPayoutHistory = async (page: number, pageLimit: number) => {
    // Prevent duplicate calls
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      const response = await payoutService.getAffiliatePayoutHistory(page, pageLimit);

      // Set pagination data
      setTotalPages(response.pagination.totalPages);
      setTotal(response.pagination.total);

      // Map the data to match PayoutHistoryItem structure
      const mappedData = response.data.map((item) => {
        // Get payment method information from the actual API response
        const paymentMethodName = item.paymentDetails?.paymentMethodName || "---";
        const paymentInformation = item.paymentDetails?.fieldData
          ? Object.values(item.paymentDetails.fieldData)[0] || "---"
          : "---";

        return {
          id: item.id,
          affiliateId: item.affiliateId,
          name: `${item.affiliate.firstName} ${item.affiliate.lastName}`,
          username: item.affiliate.firstName, // Using firstName as username since username is not available
          avatar: item.affiliate.profileImage || "",
          email: item.affiliate.email,
          payoutStatus: item.status === "PAID" ? "Paid" : "Pending",
          status: item.status, // Include the original status from API response
          paymentMethod: paymentMethodName,
          paymentInformation: paymentInformation,
          code: item.code,
          amount: item.amount,
          breakdown: item.breakdown ?? null,
          payoutDate: item.createdAt,
          paidAt: item.paidAt,
          referenceNumber: item.referenceNumber,
          company: {
            companyName: item.company.companyName,
            brandProfileImage: item.company.brandProfileImage || "",
            description: item.company.description || "",
          },
          // Include the original payment details for the detail view
          paymentDetails: item.paymentDetails,
        };
      });
      setPayoutHistory(mappedData);
    } catch (error) {
      console.error("Failed to fetch payout history:", error);
      toast.error("Failed to load payout history");
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <main>
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">Payout history</h1>
        </div>

        {/* Table */}
        <div>
          <div className="rounded-xl border border-border bg-card shadow-sm">
            <DataTable
              columns={columns}
              data={payoutHistory}
              isLoading={isLoading}
              emptyText="No payout history available"
              onRowClick={(payout) =>
                navigate(`/affiliate/payouts/history/${payout.id}`, {
                  state: { payout },
                })
              }
            />
            <DataTablePagination
              page={currentPage}
              totalPages={totalPages}
              total={total}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
              pageSizeOptions={[10, 20, 50, 100]}
              noun="payouts"
            />
          </div>
        </div>
    </main>
  );
}
